import './i18n/autoTranslate';
import React, { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  Award,
  Bookmark,
  Briefcase,
  Building2,
  CheckCircle2,
  ExternalLink,
  FileText,
  Loader2,
  MapPin,
  PlayCircle,
  RefreshCcw,
  Search,
  ShieldCheck,
  Sparkles,
  Target,
  Wand2,
  Zap,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import AppBackground from "../components/ui/AppBackground";
import { useSelector } from "react-redux";
import SEO from "./SEO";

/**
 * JobMatchAIPage.jsx
 * ------------------------------------------------------------
 * Drop-in page for InterviewGenieAI.
 *
 * What it does:
 * 1) Reads the logged-in user's saved `resume_data`.
 * 2) Extracts titles, skills, location, seniority, and keywords.
 * 3) Searches matching jobs.
 *    - First tries your Python backend endpoint:
 *      POST /api/jobs/match-from-resume
 *    - If that endpoint does not exist yet, falls back to Remotive public jobs API.
 * 4) Ranks jobs locally with a transparent match score.
 * 5) Gives apply links.
 * 6) Allows "Practice for this job" by saving finalInterviewConfiguration to localStorage.
 *
 * Add route:
 * <Route path="/job-match-ai" element={<JobMatchAIPage />} />
 */

/* -------------------- API / Storage Helpers -------------------- */

const API_BASE = (
  import.meta.env.VITE_API_URL ||
  import.meta.env.VITE_BACKEND_URL ||
  import.meta.env.VITE_SERVER_URL ||
  ""
).replace(/\/$/, "");

const RESUME_PAGE_ROUTE =
  import.meta.env.VITE_RESUME_PAGE_ROUTE ||
  import.meta.env.VITE_CV_PAGE_ROUTE ||
  "/cv-builder";

const AUTH_TOKEN_KEYS = [
  "token",
  "accessToken",
  "access_token",
  "authToken",
  "interviewgenie_token",
];

const STORED_USER_KEYS = [
  "user",
  "authUser",
  "currentUser",
  "interviewgenie_user",
  "ig_user",
];

const getToken = () => {
  for (const key of AUTH_TOKEN_KEYS) {
    const value = localStorage.getItem(key);
    if (value) return value;
  }
  return "";
};

const getAuthHeaders = () => {
  const token = getToken();
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const safeJsonParse = (value, fallback = null) => {
  try {
    if (!value) return fallback;
    if (typeof value === "object") return value;
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

const extractStorageValuesText = (input, depth = 0) => {
  if (depth > 6 || input == null) return "";
  if (typeof input === "string") return input;
  if (typeof input === "number" || typeof input === "boolean") return String(input);
  if (Array.isArray(input)) return input.map((item) => extractStorageValuesText(item, depth + 1)).join(" ");
  if (typeof input === "object") {
    return Object.values(input)
      .map((value) => extractStorageValuesText(value, depth + 1))
      .join(" ");
  }
  return "";
};

const readStoredUser = () => {
  for (const key of STORED_USER_KEYS) {
    const parsed = safeJsonParse(localStorage.getItem(key));
    if (parsed && typeof parsed === "object") return parsed;
  }
  return null;
};

const readBestStoredUser = () => {
  const users = STORED_USER_KEYS
    .map((key) => safeJsonParse(localStorage.getItem(key)))
    .filter((item) => item && typeof item === "object");

  const resumeFromCache = safeJsonParse(localStorage.getItem("ig_resume_data"));
  const userWithResume = users.find((item) => hasUsefulResumePayload(getResumePayloadFromUser(item)));
  const baseUser = userWithResume || users[0] || {};
  const bestResume = hasUsefulResumePayload(getResumePayloadFromUser(baseUser))
    ? getResumePayloadFromUser(baseUser)
    : resumeFromCache;

  return {
    ...baseUser,
    resume_data: bestResume,
    resumeData: bestResume,
  };
};

const getResumePayloadFromUser = (user = {}) =>
  user?.resume_data || user?.resumeData || user?.cv_data || user?.cvData || null;

const hasUsefulResumePayload = (payload) => {
  if (!payload) return false;

  const parsed = typeof payload === 'string' ? safeJsonParse(payload, payload) : payload;

  if (!parsed) return false;
  if (typeof parsed === 'string') return parsed.trim().length > 20;
  if (Array.isArray(parsed)) return parsed.length > 0;

  if (typeof parsed === 'object') {
    const valuesText = extractStorageValuesText(parsed).replace(/\s+/g, ' ').trim();
    const skills = Array.isArray(parsed.skills) ? parsed.skills : [];
    const work = Array.isArray(parsed.workExperience) ? parsed.workExperience : [];
    const education = Array.isArray(parsed.education) ? parsed.education : [];

    return Boolean(
      valuesText.length > 20 ||
        skills.length > 0 ||
        work.length > 0 ||
        education.length > 0
    );
  }

  return false;
};

const buildUserWithLatestResume = (reduxUser, storedUser) => {
  const reduxResume = getResumePayloadFromUser(reduxUser);
  const storedResume = getResumePayloadFromUser(storedUser);

  const bestResume = hasUsefulResumePayload(reduxResume) ? reduxResume : storedResume;

  return {
    ...(storedUser || {}),
    ...(reduxUser || {}),
    resume_data: bestResume,
    resumeData: bestResume,
  };
};

const stripHtml = (html = "") =>
  String(html)
    .replace(/<style[\s\S]*?<\/style>/gi, " ")
    .replace(/<script[\s\S]*?<\/script>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, " ")
    .trim();

const clamp = (value, min = 0, max = 100) =>
  Math.max(min, Math.min(max, Number.isFinite(value) ? value : 0));

const unique = (arr = []) =>
  [...new Set(arr.map((x) => String(x || "").trim()).filter(Boolean))];

const titleCase = (value = "") =>
  String(value)
    .split(/\s+/)
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1).toLowerCase() : ""))
    .join(" ");

const compactText = (value = "", limit = 180) => {
  const text = stripHtml(value);
  return text.length > limit ? `${text.slice(0, limit).trim()}…` : text;
};

/* -------------------- Resume Extraction -------------------- */

const SKILL_ALIASES = {
  react: ["react", "react.js", "reactjs"],
  javascript: ["javascript", "js"],
  typescript: ["typescript", "ts"],
  python: ["python", "py"],
  flask: ["flask"],
  django: ["django"],
  node: ["node", "node.js", "nodejs"],
  express: ["express", "express.js"],
  mysql: ["mysql"],
  postgresql: ["postgres", "postgresql"],
  mongodb: ["mongodb", "mongo"],
  prisma: ["prisma"],
  sqlalchemy: ["sqlalchemy"],
  redis: ["redis"],
  celery: ["celery"],
  tailwind: ["tailwind", "tailwindcss", "tailwind css"],
  html: ["html"],
  css: ["css"],
  "next.js": ["next.js", "nextjs", "next"],
  docker: ["docker"],
  kubernetes: ["kubernetes", "k8s"],
  aws: ["aws", "amazon web services"],
  azure: ["azure"],
  gcp: ["gcp", "google cloud"],
  git: ["git", "github", "gitlab"],
  rest: ["rest", "rest api", "api"],
  graphql: ["graphql"],
  figma: ["figma"],
  "ui/ux": ["ui/ux", "ux", "ui design", "product design"],
  seo: ["seo"],
  "machine learning": ["machine learning", "ml"],
  "data analysis": ["data analysis", "analytics"],
  "cloudflare r2": ["cloudflare r2", "r2"],
  stripe: ["stripe"],
  paypal: ["paypal"],
};

const TITLE_PATTERNS = [
  "full stack developer",
  "full-stack developer",
  "frontend developer",
  "front-end developer",
  "backend developer",
  "back-end developer",
  "software engineer",
  "web developer",
  "react developer",
  "python developer",
  "node.js developer",
  "javascript developer",
  "product designer",
  "ui ux designer",
  "data analyst",
  "devops engineer",
  "qa engineer",
  "mobile developer",
  "project manager",
  "product manager",
  "digital marketer",
];

const extractAllText = (input, depth = 0) => {
  if (depth > 5 || input == null) return "";

  if (typeof input === "string") {
    const parsed = safeJsonParse(input);
    if (parsed && typeof parsed === "object") return extractAllText(parsed, depth + 1);
    return input;
  }

  if (typeof input === "number" || typeof input === "boolean") return String(input);

  if (Array.isArray(input)) {
    return input.map((x) => extractAllText(x, depth + 1)).join(" ");
  }

  if (typeof input === "object") {
    return Object.entries(input)
      .map(([key, value]) => `${key} ${extractAllText(value, depth + 1)}`)
      .join(" ");
  }

  return "";
};

const pickFirstField = (obj, keys) => {
  if (!obj || typeof obj !== "object") return "";
  for (const key of keys) {
    const direct = obj[key];
    if (typeof direct === "string" && direct.trim()) return direct.trim();
    if (Array.isArray(direct) && direct.length) return direct.join(", ");
  }
  return "";
};

const extractProfileFromResume = (resumeData, user = {}) => {
  const normalized =
    typeof resumeData === "string" ? safeJsonParse(resumeData, resumeData) : resumeData;

  const rawText = extractAllText(normalized);
  const text = rawText.toLowerCase();

  const detectedSkills = [];

  Object.entries(SKILL_ALIASES).forEach(([canonical, aliases]) => {
    if (aliases.some((alias) => text.includes(alias.toLowerCase()))) {
      detectedSkills.push(canonical);
    }
  });

  const directSkills = [];
  if (normalized && typeof normalized === "object") {
    const skillFields = [
      normalized.skills,
      normalized.technical_skills,
      normalized.technicalSkills,
      normalized.key_skills,
      normalized.extracted_skills,
      normalized.extractedSkills,
      normalized.resume_skills,
      normalized.skillsList,
    ];

    skillFields.forEach((field) => {
      if (Array.isArray(field)) directSkills.push(...field);
      if (typeof field === "string") directSkills.push(...field.split(/[,|•\n]/));
    });
  }

  const skills = unique([...directSkills, ...detectedSkills])
    .map((s) => String(s).replace(/^[-•]\s*/, "").trim())
    .filter((s) => s.length >= 2)
    .slice(0, 28);

  const detectedTitles = TITLE_PATTERNS.filter((title) => text.includes(title));
  const directTitle = pickFirstField(normalized, [
    "target_role",
    "targetRole",
    "job_title",
    "jobTitle",
    "title",
    "role",
    "current_title",
    "currentTitle",
    "desired_role",
    "desiredRole",
    "profession",
  ]);

  let titles = unique([directTitle, ...detectedTitles])
    .filter(Boolean)
    .map((t) => titleCase(t.replace("-", " ")));

  if (!titles.length) {
    if (skills.includes("react") && (skills.includes("python") || skills.includes("node"))) {
      titles = ["Full Stack Developer"];
    } else if (skills.includes("react") || skills.includes("javascript")) {
      titles = ["Frontend Developer"];
    } else if (skills.includes("python") || skills.includes("flask") || skills.includes("node")) {
      titles = ["Backend Developer"];
    } else {
      titles = ["Software Developer"];
    }
  }

  const location =
    pickFirstField(normalized, ["location", "city", "country", "address"]) ||
    user?.country ||
    (text.includes("morocco") || text.includes("maroc") ? "Morocco" : "Remote");

  const yearsMatch =
    text.match(/(\d+)\+?\s*(years|year|ans|année|années)/i) ||
    text.match(/experience\s*[:\-]?\s*(\d+)/i);

  const years = yearsMatch ? Number(yearsMatch[1]) : 0;
  const seniority =
    years >= 5
      ? "Senior"
      : years >= 2
        ? "Mid-level"
        : text.includes("intern")
          ? "Internship"
          : "Junior / Entry";

  const summary =
    pickFirstField(normalized, ["summary", "professional_summary", "profile", "about"]) ||
    compactText(rawText, 220);

  return {
    rawText,
    skills,
    titles: titles.slice(0, 5),
    location,
    years,
    seniority,
    summary,
  };
};

const buildSearchQuery = (profile, filters = {}) => {
  const title = filters.role || profile.titles?.[0] || "Software Developer";
  const topSkills = (profile.skills || []).slice(0, 5).join(" ");
  const mode = filters.workMode === "remote" ? "remote" : "";
  return [title, topSkills, mode].filter(Boolean).join(" ").trim();
};

/* -------------------- Job Search + Matching -------------------- */

const normalizeBackendJob = (job = {}, index = 0) => ({
  id: String(job.id || job.external_id || job.externalId || `backend-${index}`),
  source: job.source || "InterviewGenieAI",
  title: job.title || job.job_title || "Untitled role",
  company: job.company || job.company_name || job.employer_name || "Unknown company",
  location: job.location || job.job_city || job.candidate_required_location || "Remote",
  type: job.job_type || job.type || job.employment_type || "Full-time",
  salary: job.salary || job.salary_range || job.job_salary || "",
  description: stripHtml(job.description || job.job_description || job.snippet || ""),
  applyUrl: job.apply_url || job.applyUrl || job.url || job.job_apply_link || job.link || "#",
  postedAt: job.publication_date || job.posted_at || job.date || "",
  logo: job.logo || job.company_logo || "",
  remote: String(job.location || "").toLowerCase().includes("remote"),
  original: job,
});

const fetchBackendJobs = async ({ resumeData, profile, filters }) => {
  if (!API_BASE) return null;

  const response = await fetch(`${API_BASE}/api/jobs/match-from-resume`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({
      resume_data: resumeData,
      resumeData,
      profile,
      query: buildSearchQuery(profile, filters),
      location: filters.location || profile.location,
      workMode: filters.workMode,
      limit: 40,
    }),
  });

  if (!response.ok) return null;

  const data = await response.json();
  const jobs = data.jobs || data.results || data.recommendations || [];

  if (!Array.isArray(jobs)) return null;

  return jobs.map(normalizeBackendJob);
};

const fetchRemotiveJobs = async ({ profile, filters }) => {
  const query = buildSearchQuery(profile, filters);
  const response = await fetch(
    `https://remotive.com/api/remote-jobs?search=${encodeURIComponent(query)}&limit=60`
  );

  if (!response.ok) throw new Error("Job search service is not reachable right now.");

  const data = await response.json();

  return (data.jobs || []).map((job, index) => ({
    id: String(job.id || `remotive-${index}`),
    source: "Remotive",
    title: job.title || "Untitled role",
    company: job.company_name || "Unknown company",
    location: job.candidate_required_location || "Remote",
    type: job.job_type || "Full-time",
    salary: job.salary || "",
    description: stripHtml(job.description || ""),
    applyUrl: job.url || "#",
    postedAt: job.publication_date || "",
    logo: job.company_logo || "",
    remote: true,
    original: job,
  }));
};

const FALLBACK_JOBS = [
  {
    id: "fallback-1",
    source: "Demo",
    title: "Full Stack Developer",
    company: "Remote SaaS Studio",
    location: "Remote",
    type: "Full-time",
    salary: "",
    description:
      "Build modern SaaS features with React, Python, APIs, SQL databases, authentication, dashboards, and clean production UX.",
    applyUrl: "https://remotive.com/remote-jobs/software-dev",
    postedAt: "",
    remote: true,
  },
  {
    id: "fallback-2",
    source: "Demo",
    title: "Frontend React Developer",
    company: "Product UI Lab",
    location: "Remote",
    type: "Contract",
    salary: "",
    description:
      "Create premium React and Tailwind user interfaces, reusable components, dashboards, landing pages, and performance-focused web experiences.",
    applyUrl: "https://remotive.com/remote-jobs/software-dev",
    postedAt: "",
    remote: true,
  },
  {
    id: "fallback-3",
    source: "Demo",
    title: "Backend Python Developer",
    company: "AI Platform Team",
    location: "Remote",
    type: "Full-time",
    salary: "",
    description:
      "Design Flask APIs, authentication flows, database models, background jobs, integrations, and reliable backend services.",
    applyUrl: "https://remotive.com/remote-jobs/software-dev",
    postedAt: "",
    remote: true,
  },
];

const scoreJob = (job, profile, filters = {}) => {
  const text = `${job.title} ${job.company} ${job.location} ${job.description}`.toLowerCase();
  const titleText = String(job.title || "").toLowerCase();

  const skills = profile.skills || [];
  const matchedSkills = skills.filter((skill) => {
    const aliases = SKILL_ALIASES[skill.toLowerCase()] || [skill.toLowerCase()];
    return aliases.some((alias) => text.includes(alias.toLowerCase()));
  });

  const missingSkills = Object.keys(SKILL_ALIASES)
    .filter((skill) => text.includes(skill) && !skills.map((s) => s.toLowerCase()).includes(skill))
    .slice(0, 6);

  const titleScore = Math.max(
    ...profile.titles.map((title) => {
      const words = title.toLowerCase().split(/\s+/).filter((w) => w.length > 2);
      const hits = words.filter((w) => titleText.includes(w)).length;
      return words.length ? (hits / words.length) * 28 : 0;
    }),
    0
  );

  const skillsScore = Math.min(50, matchedSkills.length * 8.5);

  const locationTarget = String(filters.location || profile.location || "").toLowerCase();
  const jobLocation = String(job.location || "").toLowerCase();
  const remoteWanted = filters.workMode === "remote";

  let locationScore = 0;
  if (remoteWanted && (job.remote || jobLocation.includes("remote") || jobLocation.includes("worldwide"))) {
    locationScore = 14;
  } else if (locationTarget && jobLocation.includes(locationTarget)) {
    locationScore = 14;
  } else if (jobLocation.includes("remote")) {
    locationScore = 9;
  }

  let seniorityScore = 4;
  const seniority = String(profile.seniority || "").toLowerCase();
  if (seniority.includes("junior") && !/senior|lead|principal|staff/i.test(text)) seniorityScore = 8;
  if (seniority.includes("mid") && !/intern/i.test(text)) seniorityScore = 8;
  if (seniority.includes("senior") && /senior|lead|principal|staff/i.test(text)) seniorityScore = 8;

  const score = clamp(Math.round(titleScore + skillsScore + locationScore + seniorityScore), 18, 98);

  const reasons = [];
  if (matchedSkills.length) reasons.push(`Matches ${matchedSkills.slice(0, 5).join(", ")}`);
  if (titleScore >= 14) reasons.push("Role title is close to your CV");
  if (locationScore >= 9) reasons.push(remoteWanted ? "Remote-friendly" : "Good location fit");
  if (!reasons.length) reasons.push("Related role based on your resume profile");

  return {
    ...job,
    matchScore: score,
    matchedSkills,
    missingSkills,
    reasons,
  };
};

const getMatchTone = (score) => {
  if (score >= 82) return "green";
  if (score >= 65) return "purple";
  if (score >= 45) return "amber";
  return "neutral";
};

/* -------------------- Premium UI Tokens -------------------- */

const GlowBackdrop = () => (
  <div
    aria-hidden
    className="pointer-events-none absolute inset-0
    bg-[radial-gradient(55%_45%_at_50%_8%,rgba(108,99,255,0.22),transparent_62%),
        radial-gradient(35%_30%_at_86%_78%,rgba(16,185,129,0.16),transparent_60%),
        radial-gradient(30%_28%_at_14%_82%,rgba(236,72,153,0.10),transparent_62%)]"
  />
);

const HairlineGrid = () => (
  <div
    aria-hidden
    className="pointer-events-none absolute inset-0 opacity-30
    [background-image:radial-gradient(rgba(255,255,255,0.08)_1px,transparent_1px)]
    [background-size:26px_26px]"
  />
);

const Glass = ({ className = "", children }) => (
  <div
    className={[
      "relative rounded-[28px] border border-white/10",
      "bg-white/[0.045] backdrop-blur-2xl",
      "shadow-[0_20px_60px_rgba(0,0,0,0.55)] overflow-hidden",
      className,
    ].join(" ")}
  >
    <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(255,255,255,0.10),transparent_55%)]" />
    <div className="absolute inset-0 bg-[radial-gradient(900px_420px_at_60%_0%,rgba(255,255,255,0.08),transparent_62%)]" />
    <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-[#6C63FF]/12 blur-3xl" />
    <div className="absolute -bottom-28 -left-24 h-72 w-72 rounded-full bg-emerald-500/10 blur-3xl" />
    <div className="relative">{children}</div>
  </div>
);

const Chip = ({ icon: Icon, children, tone = "neutral", className = "" }) => {
  const map = {
    neutral: "bg-white/5 border-white/10 text-white/75",
    purple: "bg-[#6C63FF]/10 border-[#6C63FF]/20 text-[#D7D4FF]",
    green: "bg-emerald-500/10 border-emerald-500/20 text-emerald-200",
    amber: "bg-amber-500/10 border-amber-500/20 text-amber-200",
    red: "bg-red-500/10 border-red-500/20 text-red-200",
  };

  return (
    <span
      className={[
        "inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-[11px] font-semibold border tracking-wide",
        map[tone] || map.neutral,
        className,
      ].join(" ")}
    >
      {Icon ? <Icon className="h-4 w-4 opacity-90" /> : null}
      <span className="truncate">{children}</span>
    </span>
  );
};

const PrimaryButton = ({ className = "", children, ...props }) => (
  <button
    {...props}
    className={[
      "h-12 px-5 rounded-2xl font-semibold text-white transition inline-flex items-center justify-center gap-2",
      "bg-[linear-gradient(135deg,rgba(108,99,255,0.95),rgba(16,185,129,0.55))]",
      "shadow-[0_0_90px_rgba(108,99,255,0.18)] hover:shadow-[0_0_130px_rgba(108,99,255,0.26)]",
      "border border-white/10 disabled:opacity-50 disabled:cursor-not-allowed",
      "active:translate-y-[1px] active:shadow-[0_0_70px_rgba(108,99,255,0.14)]",
      className,
    ].join(" ")}
  >
    {children}
  </button>
);

const GhostButton = ({ className = "", children, ...props }) => (
  <button
    {...props}
    className={[
      "h-12 px-5 rounded-2xl font-semibold transition inline-flex items-center justify-center gap-2",
      "bg-white/5 border border-white/10 text-white/80 hover:bg-white/10",
      "disabled:opacity-50 disabled:cursor-not-allowed active:translate-y-[1px]",
      className,
    ].join(" ")}
  >
    {children}
  </button>
);

const MetricBar = ({ label, value = 0, hint }) => {
  const v = clamp(Math.round(value), 0, 100);

  return (
    <div className="rounded-2xl bg-white/5 border border-white/10 p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[11px] font-semibold text-white/55 uppercase tracking-[0.24em]">
            {label}
          </div>
          {hint ? <div className="mt-1 text-xs text-white/50">{hint}</div> : null}
        </div>
        <div className="text-xl font-semibold text-white tabular-nums">{v}%</div>
      </div>
      <div className="mt-3 h-2 rounded-full bg-white/5 border border-white/10 overflow-hidden">
        <div
          className="h-full rounded-full"
          style={{
            width: `${v}%`,
            backgroundImage:
              "linear-gradient(90deg, rgba(108,99,255,0.90), rgba(16,185,129,0.78))",
          }}
        />
      </div>
    </div>
  );
};

const ScoreBadge = ({ score }) => {
  const tone = getMatchTone(score);
  const toneClass =
    tone === "green"
      ? "bg-emerald-500/10 border-emerald-400/20 text-emerald-200"
      : tone === "purple"
        ? "bg-[#6C63FF]/10 border-[#6C63FF]/25 text-[#D7D4FF]"
        : tone === "amber"
          ? "bg-amber-500/10 border-amber-400/25 text-amber-200"
          : "bg-white/5 border-white/10 text-white/70";

  return (
    <div
      className={[
        "h-16 w-16 rounded-[24px] border flex flex-col items-center justify-center shrink-0",
        toneClass,
      ].join(" ")}
    >
      <div className="text-xl font-bold tabular-nums">{score}</div>
      <div className="text-[9px] uppercase tracking-[0.22em] opacity-70">match</div>
    </div>
  );
};

const MiniWave = ({ active = true }) => {
  const bars = Array.from({ length: 18 }, (_, i) => i);

  return (
    <div className="flex items-end justify-center gap-1.5 h-10">
      {bars.map((i) => {
        const height = Math.max(
          7,
          Math.round(10 + (Math.sin(i * 1.07) + 1) * (active ? 13 : 3))
        );

        return (
          <div
            key={i}
            className={[
              "w-[5px] rounded-full transition-all duration-200",
              active ? "bg-[#6C63FF]/70 animate-pulse" : "bg-white/25",
            ].join(" ")}
            style={{
              height,
              animationDelay: `${(i % 7) * 35}ms`,
            }}
          />
        );
      })}
    </div>
  );
};

/* -------------------- Panels -------------------- */

const EmptyResumeState = ({ onRefresh, onResumeClick }) => (
  <Glass className="max-w-3xl mx-auto">
    <GlowBackdrop />
    <HairlineGrid />
    <div className="p-8 sm:p-10 text-center">
      <div className="mx-auto h-16 w-16 rounded-[24px] bg-amber-500/10 border border-amber-400/20 flex items-center justify-center">
        <FileText className="h-8 w-8 text-amber-200" />
      </div>
      <h1 className="mt-5 text-3xl font-semibold text-white tracking-tight">
        Add your resume first
      </h1>
      <p className="mt-3 text-white/60 leading-relaxed">
        JobMatch AI needs your saved <span className="text-white font-semibold">resume_data</span> to detect skills,
        roles, and location. Upload or complete your resume profile, then refresh this page.
      </p>
      <div className="mt-6 flex flex-col sm:flex-row justify-center gap-3">
        <PrimaryButton onClick={onResumeClick}>
          <FileText className="h-4 w-4" />
          Go to Resume
        </PrimaryButton>
        <GhostButton onClick={onRefresh}>
          <RefreshCcw className="h-4 w-4" />
          Refresh
        </GhostButton>
      </div>
    </div>
  </Glass>
);

const ProfilePanel = ({ profile, stats }) => (
  <Glass className="h-full">
    <GlowBackdrop />
    <HairlineGrid />
    <div className="p-6 h-full flex flex-col">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-sm font-semibold text-white tracking-tight">CV Intelligence</div>
          <div className="mt-1 text-xs text-white/55">
            Resume-driven job matching • private ranking layer
          </div>
        </div>
        <Chip icon={ShieldCheck}>Private</Chip>
      </div>

      <div className="mt-5 rounded-2xl bg-white/5 border border-white/10 p-5">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-2xl bg-[#6C63FF]/10 border border-[#6C63FF]/20 flex items-center justify-center">
            <Target className="h-6 w-6 text-[#D7D4FF]" />
          </div>
          <div className="min-w-0">
            <div className="text-lg font-semibold text-white truncate">
              {profile.titles?.[0] || "Target role"}
            </div>
            <div className="text-xs text-white/50 truncate">
              {profile.seniority} • {profile.location || "Remote"}
            </div>
          </div>
        </div>

        <p className="mt-4 text-sm text-white/65 leading-relaxed line-clamp-5">
          {profile.summary || "Resume profile detected. JobMatch AI will use your skills and target role to rank jobs."}
        </p>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3">
        <MetricBar label="Profile Fit" value={stats.profileStrength} hint="Detected data" />
        <MetricBar label="Skill Depth" value={stats.skillDepth} hint={`${profile.skills.length} skills`} />
      </div>

      <div className="mt-4 rounded-2xl bg-[#0B0B12]/35 border border-white/10 p-5">
        <div className="flex items-center justify-between gap-3">
          <div className="text-[11px] font-semibold text-white/55 uppercase tracking-[0.24em]">
            Top skills
          </div>
          <Chip tone="purple" icon={Sparkles}>
            {profile.skills.length} found
          </Chip>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {profile.skills.slice(0, 18).map((skill) => (
            <Chip key={skill} tone="neutral">
              {skill}
            </Chip>
          ))}

          {!profile.skills.length && (
            <div className="text-sm text-white/50">No clear skills detected yet.</div>
          )}
        </div>
      </div>

      <div className="mt-4 rounded-2xl bg-white/5 border border-white/10 p-5">
        <div className="text-[11px] font-semibold text-white/55 uppercase tracking-[0.24em]">
          Suggested roles
        </div>
        <div className="mt-3 space-y-2">
          {profile.titles.map((title) => (
            <div key={title} className="flex items-center gap-2 text-sm text-white/70">
              <CheckCircle2 className="h-4 w-4 text-emerald-200" />
              {title}
            </div>
          ))}
        </div>
      </div>

      <div className="mt-auto pt-5 text-xs text-white/40">
        Tip: add measurable projects to your CV to improve matching accuracy.
      </div>
    </div>
  </Glass>
);

const StrategyPanel = ({ selectedJob, bestJobsCount, onPractice }) => (
  <Glass className="h-full">
    <GlowBackdrop />
    <HairlineGrid />
    <div className="p-6 h-full flex flex-col">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-sm font-semibold text-white tracking-tight">Application Strategy</div>
          <div className="mt-1 text-xs text-white/55">
            Apply smarter • then practice for the exact job
          </div>
        </div>
        <Chip icon={Wand2} tone="purple">AI Plan</Chip>
      </div>

      <div className="mt-5 rounded-2xl bg-white/5 border border-white/10 p-5">
        <div className="text-[11px] font-semibold text-white/55 uppercase tracking-[0.24em]">
          Best action now
        </div>

        {selectedJob ? (
          <>
            <div className="mt-3 text-lg font-semibold text-white leading-snug">
              Prepare for {selectedJob.title}
            </div>
            <div className="mt-2 text-sm text-white/60 leading-relaxed">
              This role matches your profile at{" "}
              <span className="text-white font-semibold">{selectedJob.matchScore}%</span>. Practice with its title,
              company context, and job description before applying.
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              {selectedJob.reasons.map((reason) => (
                <Chip key={reason} icon={CheckCircle2} tone="green">
                  {reason}
                </Chip>
              ))}
            </div>

            <PrimaryButton onClick={() => onPractice(selectedJob)} className="mt-5 w-full">
              <PlayCircle className="h-4 w-4" />
              Practice for selected job
            </PrimaryButton>
          </>
        ) : (
          <div className="mt-3 text-sm text-white/60 leading-relaxed">
            Select a job from the list to generate a focused interview configuration.
          </div>
        )}
      </div>

      <div className="mt-4 rounded-2xl bg-[#0B0B12]/35 border border-white/10 p-5">
        <div className="text-[11px] font-semibold text-white/55 uppercase tracking-[0.24em]">
          Match checklist
        </div>

        <div className="mt-4 space-y-4">
          <ChecklistItem
            ok={bestJobsCount > 0}
            label={`${bestJobsCount} strong matches`}
            meta="Prioritize jobs above 70% match."
          />
          <ChecklistItem
            ok={!!selectedJob?.matchedSkills?.length}
            label="Use matched skills in cover letter"
            meta={selectedJob?.matchedSkills?.slice(0, 4).join(", ") || "Select a job first."}
          />
          <ChecklistItem
            ok={!!selectedJob?.missingSkills?.length}
            label="Fix missing skill gap"
            meta={selectedJob?.missingSkills?.slice(0, 3).join(", ") || "No major gap detected."}
          />
          <ChecklistItem
            ok={!!selectedJob}
            label="Practice before applying"
            meta="Job-specific AI interview increases preparation quality."
          />
        </div>
      </div>

      <div className="mt-4 rounded-2xl bg-white/5 border border-white/10 p-5">
        <div className="flex items-center gap-2 text-[11px] font-semibold text-white/55 uppercase tracking-[0.24em]">
          <Zap className="h-4 w-4 text-[#D7D4FF]" />
          Conversion idea
        </div>
        <div className="mt-2 text-sm text-white/70 leading-relaxed">
          Add this to your pricing: Free users get 5 job matches/month, Pro users get unlimited matches + cover letter +
          job-specific interview.
        </div>
      </div>

      <div className="mt-auto pt-5 text-xs text-white/40">
        Job links open in a new tab. InterviewGenieAI stores only the selected job configuration locally.
      </div>
    </div>
  </Glass>
);

const ChecklistItem = ({ ok, label, meta }) => (
  <div className="flex items-start gap-3">
    <span
      className={[
        "mt-0.5 h-6 w-6 rounded-full border flex items-center justify-center shrink-0",
        ok ? "bg-emerald-500/10 border-emerald-500/25" : "bg-white/5 border-white/10",
      ].join(" ")}
    >
      {ok ? <CheckCircle2 className="h-4 w-4 text-emerald-200" /> : <div className="h-2 w-2 rounded-full bg-white/25" />}
    </span>
    <div className="min-w-0">
      <div className="text-sm text-white/80 font-medium leading-snug">{label}</div>
      {meta ? <div className="mt-1 text-xs text-white/55 leading-snug">{meta}</div> : null}
    </div>
  </div>
);

const JobCard = ({ job, active, onSelect, onPractice, onSave }) => {
  const tone = getMatchTone(job.matchScore);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect(job)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect(job);
        }
      }}
      className={[
        "w-full text-left rounded-[28px] border p-4 transition relative overflow-hidden cursor-pointer outline-none",
        "focus-visible:ring-2 focus-visible:ring-[#6C63FF]/60 focus-visible:ring-offset-0",
        active
          ? "bg-white/[0.075] border-[#6C63FF]/40 shadow-[0_0_70px_rgba(108,99,255,0.16)]"
          : "bg-white/[0.045] border-white/10 hover:bg-white/[0.065] hover:border-white/15",
      ].join(" ")}
    >
      <div className="absolute inset-0 bg-[radial-gradient(600px_180px_at_0%_0%,rgba(108,99,255,0.12),transparent_58%)]" />

      <div className="relative flex items-start gap-4">
        <ScoreBadge score={job.matchScore} />

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[15px] font-semibold text-white leading-snug line-clamp-2">
                {job.title}
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-white/50">
                <span className="inline-flex items-center gap-1">
                  <Building2 className="h-3.5 w-3.5" />
                  {job.company}
                </span>
                <span className="inline-flex items-center gap-1">
                  <MapPin className="h-3.5 w-3.5" />
                  {job.location}
                </span>
              </div>
            </div>

            <Chip tone={tone}>{job.source}</Chip>
          </div>

          <p className="mt-3 text-xs text-white/60 leading-relaxed line-clamp-3">
            {job.description || "No description available."}
          </p>

          <div className="mt-3 flex flex-wrap gap-2">
            {job.matchedSkills.slice(0, 5).map((skill) => (
              <Chip key={skill} tone="green" className="max-w-[130px]">
                {skill}
              </Chip>
            ))}
            {job.type ? <Chip tone="neutral">{job.type}</Chip> : null}
            {job.salary ? <Chip tone="amber">{job.salary}</Chip> : null}
          </div>

          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2">
            <a
              href={job.applyUrl}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="h-10 rounded-2xl bg-white text-black text-xs font-bold flex items-center justify-center gap-1 hover:bg-white/90 transition"
            >
              Apply <ExternalLink className="h-3.5 w-3.5" />
            </a>

            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onPractice(job);
              }}
              className="h-10 rounded-2xl bg-[#6C63FF]/15 border border-[#6C63FF]/25 text-[#D7D4FF] text-xs font-bold flex items-center justify-center gap-1 hover:bg-[#6C63FF]/20 transition"
            >
              Practice
            </button>

            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onSave(job);
              }}
              className="h-10 rounded-2xl bg-white/5 border border-white/10 text-white/75 text-xs font-bold flex items-center justify-center gap-1 hover:bg-white/10 transition"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* -------------------- Main Page -------------------- */

export default function JobMatchAIPage() {
  const navigate = useNavigate();

  // Redux auth user.
  // Supports these common store shapes:
  // state.auth.user
  // state.user.user
  // state.user
  // state.auth.currentUser
  const { user: selectedUser } = useSelector((state) => ({
    user:
      state?.auth?.user ||
      state?.user?.user ||
      state?.user ||
      state?.auth?.currentUser ||
      null,
  }));

  const storedUser = readBestStoredUser();
  const authUser = buildUserWithLatestResume(selectedUser, storedUser);

  const [refreshKey, setRefreshKey] = useState(0);
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [savedJobs, setSavedJobs] = useState(() => safeJsonParse(localStorage.getItem("ig_saved_jobs"), []));
  const [loading, setLoading] = useState(false);
  const [sourceUsed, setSourceUsed] = useState("");
  const [error, setError] = useState("");

  const [filters, setFilters] = useState({
    role: "",
    location: "",
    workMode: "remote",
    minScore: 0,
  });

  const resumeData = getResumePayloadFromUser(authUser);

  const profile = useMemo(() => {
    return extractProfileFromResume(resumeData, authUser || {});
  }, [resumeData, authUser, refreshKey]);

  const hasResume = useMemo(() => {
    const text = profile.rawText || "";
    return text.trim().length > 20 || profile.skills.length > 0;
  }, [profile]);

  const stats = useMemo(() => {
    const profileStrength = clamp(
      20 +
        Math.min(profile.skills.length, 12) * 5 +
        Math.min(profile.titles.length, 3) * 8 +
        (profile.summary ? 12 : 0) +
        (profile.location ? 8 : 0),
      0,
      100
    );

    const skillDepth = clamp(profile.skills.length * 6, 0, 100);

    return { profileStrength, skillDepth };
  }, [profile]);

  const bestJobsCount = useMemo(
    () => jobs.filter((job) => job.matchScore >= 70).length,
    [jobs]
  );

  const filteredJobs = useMemo(() => {
    return jobs
      .filter((job) => job.matchScore >= Number(filters.minScore || 0))
      .filter((job) => {
        if (!filters.location.trim()) return true;
        const location = `${job.location}`.toLowerCase();
        const wanted = filters.location.toLowerCase();
        return location.includes(wanted) || location.includes("remote");
      });
  }, [jobs, filters.minScore, filters.location]);

  const selected = selectedJob || filteredJobs[0] || null;

  useEffect(() => {
    if (hasResume) {
      runJobSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasResume, refreshKey]);

  const runJobSearch = async () => {
    setLoading(true);
    setError("");
    setSourceUsed("");

    try {
      let rawJobs = null;

      try {
        rawJobs = await fetchBackendJobs({ resumeData, profile, filters });
        if (rawJobs?.length) setSourceUsed("Backend");
      } catch {
        rawJobs = null;
      }

      if (!rawJobs?.length) {
        rawJobs = await fetchRemotiveJobs({ profile, filters });
        setSourceUsed("Remotive");
      }

      const ranked = rawJobs
        .map((job) => scoreJob(job, profile, filters))
        .sort((a, b) => b.matchScore - a.matchScore)
        .slice(0, 30);

      setJobs(ranked);
      setSelectedJob(ranked[0] || null);
    } catch (err) {
      const rankedFallback = FALLBACK_JOBS.map((job) => scoreJob(job, profile, filters)).sort(
        (a, b) => b.matchScore - a.matchScore
      );

      setJobs(rankedFallback);
      setSelectedJob(rankedFallback[0] || null);
      setSourceUsed("Demo fallback");
      setError(err?.message || "Could not fetch live jobs. Showing demo matches for now.");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveJob = (job) => {
    const next = [
      {
        ...job,
        savedAt: new Date().toISOString(),
      },
      ...savedJobs.filter((item) => !(item.id === job.id && item.source === job.source)),
    ].slice(0, 50);

    setSavedJobs(next);
    localStorage.setItem("ig_saved_jobs", JSON.stringify(next));
  };

  const handlePracticeForJob = (job) => {
    const configuration = {
      selectedType: "job-based",
      selectedIndustry: guessIndustry(job),
      selectedRole: job.title,
      selectedCompanyType: job.remote ? "Remote Company" : "Company",
      estimatedQuestions: 5,
      personality: {
        selectedPersona: {
          id: "job_match_recruiter",
          name: "Job-Specific Recruiter",
          description: `Interviewer for ${job.title} at ${job.company}`,
        },
        tone: 3,
        difficulty: profile.seniority?.toLowerCase().includes("senior") ? 4 : 3,
        pace: 3,
        pressure: 3,
        accent: "american",
      },
      selectedCategories: ["behavioral", "technical"],
      selectedCompanies: [job.company].filter(Boolean),
      interviewType: "job-based",
      customQuestions: [],
      enableFollowUps: true,
      surpriseQuestions: false,
      starMethodEnforcement: true,
      followUpIntensity: 3,
      adaptiveDifficulty: true,
      selectedFormat: "phone",
      duration: 30,
      elevenlabsVoice: null,
      jobDescription: job.description,
      jobSource: job.source,
      jobApplyUrl: job.applyUrl,
      matchedSkills: job.matchedSkills,
      missingSkills: job.missingSkills,
      matchScore: job.matchScore,
    };

    localStorage.setItem("finalInterviewConfiguration", JSON.stringify(configuration));
    localStorage.setItem("jobMatchSelectedJob", JSON.stringify(job));

    navigate("/interview-setup-configuration");
  };

  const guessIndustry = (job) => {
    const text = `${job.title} ${job.description}`.toLowerCase();
    if (/health|medical|clinic|pharma/.test(text)) return "Healthcare";
    if (/finance|bank|payment|fintech|accounting/.test(text)) return "Finance";
    if (/education|school|learning|teacher/.test(text)) return "Education";
    if (/ecommerce|shop|retail|marketplace/.test(text)) return "E-commerce";
    if (/ai|machine learning|data|model/.test(text)) return "AI / Software";
    return "Technology";
  };

  if (!hasResume) {
    return (
      <AppBackground>
        <SEO
          title="JobMatch AI — Find Jobs Based on Your CV"
          description="Find jobs that match your CV, skills, and interview goals with InterviewGenieAI."
          canonical="https://interviewgenieai.com/job-match-ai"
        />
        <div className="min-h-screen w-full relative mt-16 px-4 py-10">
          <GlowBackdrop />
          <HairlineGrid />
          <EmptyResumeState onResumeClick={() => navigate(RESUME_PAGE_ROUTE)} onRefresh={() => setRefreshKey((x) => x + 1)} />
        </div>
      </AppBackground>
    );
  }

  return (
    <AppBackground>
      <SEO
        title="JobMatch AI — Find Similar Jobs Based on Your CV"
        description="InterviewGenieAI analyzes your saved resume and recommends jobs with match scores, missing skills, apply links, and job-specific interview practice."
        canonical="https://interviewgenieai.com/job-match-ai"
      />

      <div className="min-h-screen w-full relative mt-16 overflow-hidden">
        <GlowBackdrop />
        <HairlineGrid />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-10 space-y-6">
          <Glass>
            <div className="p-6 lg:p-8">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                <div className="min-w-0">
                  <div className="inline-flex items-center gap-2 rounded-full border border-[#6C63FF]/20 bg-[#6C63FF]/10 px-3 py-1.5 text-xs font-semibold text-[#D7D4FF]">
                    <Sparkles className="h-4 w-4" />
                    JobMatch AI
                  </div>

                  <h1 className="mt-4 text-3xl sm:text-4xl font-semibold tracking-tight text-white">
                    Simple job matches from your CV
                  </h1>

                  <p className="mt-3 max-w-3xl text-sm sm:text-base text-white/60 leading-relaxed">
                    Matching jobs based on <span className="text-white font-semibold">{profile.titles?.[0]}</span>, your skills,
                    location, and seniority. Clean results, clear match scores, and quick actions.
                  </p>
                </div>

                <div className="grid grid-cols-3 gap-3 min-w-full sm:min-w-[430px] lg:min-w-[460px]">
                  <div className="rounded-2xl bg-white/5 border border-white/10 p-4 text-center">
                    <div className="text-2xl font-bold text-white tabular-nums">{filteredJobs.length}</div>
                    <div className="mt-1 text-[11px] uppercase tracking-[0.22em] text-white/45">Matches</div>
                  </div>
                  <div className="rounded-2xl bg-emerald-500/10 border border-emerald-500/20 p-4 text-center">
                    <div className="text-2xl font-bold text-emerald-100 tabular-nums">{bestJobsCount}</div>
                    <div className="mt-1 text-[11px] uppercase tracking-[0.22em] text-emerald-100/55">Strong</div>
                  </div>
                  <div className="rounded-2xl bg-white/5 border border-white/10 p-4 text-center">
                    <div className="text-2xl font-bold text-white tabular-nums">{savedJobs.length}</div>
                    <div className="mt-1 text-[11px] uppercase tracking-[0.22em] text-white/45">Saved</div>
                  </div>
                </div>
              </div>
            </div>
          </Glass>

          <Glass>
            <div className="p-4 sm:p-5">
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-[1.2fr_1fr_160px_150px_auto] gap-3">
                <input
                  value={filters.role}
                  onChange={(e) => setFilters((p) => ({ ...p, role: e.target.value }))}
                  placeholder={profile.titles?.[0] || "Role"}
                  className="h-12 rounded-2xl bg-white/5 border border-white/10 px-4 text-sm text-white placeholder:text-white/35 outline-none focus:border-[#6C63FF]/40"
                />

                <input
                  value={filters.location}
                  onChange={(e) => setFilters((p) => ({ ...p, location: e.target.value }))}
                  placeholder={profile.location || "Location"}
                  className="h-12 rounded-2xl bg-white/5 border border-white/10 px-4 text-sm text-white placeholder:text-white/35 outline-none focus:border-[#6C63FF]/40"
                />

                <select
                  value={filters.workMode}
                  onChange={(e) => setFilters((p) => ({ ...p, workMode: e.target.value }))}
                  className="h-12 rounded-2xl bg-[#0B0B12] border border-white/10 px-4 text-sm text-white outline-none focus:border-[#6C63FF]/40"
                >
                  <option value="remote">Remote</option>
                  <option value="hybrid">Hybrid</option>
                  <option value="onsite">On-site</option>
                </select>

                <select
                  value={filters.minScore}
                  onChange={(e) => setFilters((p) => ({ ...p, minScore: Number(e.target.value) }))}
                  className="h-12 rounded-2xl bg-[#0B0B12] border border-white/10 px-4 text-sm text-white outline-none focus:border-[#6C63FF]/40"
                >
                  <option value={0}>All scores</option>
                  <option value={50}>50%+</option>
                  <option value={70}>70%+</option>
                  <option value={85}>85%+</option>
                </select>

                <PrimaryButton onClick={runJobSearch} disabled={loading} className="w-full md:col-span-2 xl:col-span-1">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                  Search
                </PrimaryButton>
              </div>
            </div>
          </Glass>

          <div className="grid grid-cols-1 xl:grid-cols-[340px_minmax(0,1fr)] gap-6 items-start">
            <aside className="space-y-6 xl:sticky xl:top-24">
              <ProfilePanel profile={profile} stats={stats} />

              <Glass>
                <div className="p-5">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold text-white">Search status</div>
                      <div className="mt-1 text-xs text-white/50">{sourceUsed || "Ready"}</div>
                    </div>
                    <GhostButton onClick={runJobSearch} disabled={loading} className="h-10 px-4 rounded-xl">
                      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCcw className="h-4 w-4" />}
                      Refresh
                    </GhostButton>
                  </div>

                  {error ? (
                    <div className="mt-4 rounded-2xl border border-red-500/20 bg-red-500/10 p-4 text-sm text-red-100/80 leading-relaxed">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                        <span>{error}</span>
                      </div>
                    </div>
                  ) : null}
                </div>
              </Glass>

              <StrategyPanel
                selectedJob={selected}
                bestJobsCount={bestJobsCount}
                onPractice={handlePracticeForJob}
              />
            </aside>

            <main className="space-y-4 min-w-0">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <h2 className="text-xl font-semibold text-white">Recommended jobs</h2>
                  <p className="mt-1 text-sm text-white/50">
                    Ranked by title, skills, location, and seniority.
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Chip icon={Briefcase} tone="purple">{filteredJobs.length} jobs</Chip>
                  <Chip icon={Award} tone="green">{bestJobsCount} strong</Chip>
                  <Chip icon={Bookmark}>{savedJobs.length} saved</Chip>
                </div>
              </div>

              {loading ? (
                <Glass>
                  <div className="p-8 text-center">
                    <Loader2 className="mx-auto h-8 w-8 animate-spin text-[#D7D4FF]" />
                    <div className="mt-3 text-base font-semibold text-white">Finding similar jobs...</div>
                    <div className="mt-1 text-sm text-white/50">Reading your resume profile and ranking roles.</div>
                  </div>
                </Glass>
              ) : null}

              {!loading && filteredJobs.length === 0 ? (
                <Glass>
                  <div className="p-8 text-center">
                    <Search className="mx-auto h-8 w-8 text-white/40" />
                    <div className="mt-3 text-base font-semibold text-white">No jobs found yet</div>
                    <div className="mt-1 text-sm text-white/50">Try another role, remove filters, or refresh.</div>
                  </div>
                </Glass>
              ) : null}

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {filteredJobs.map((job) => (
                  <JobCard
                    key={`${job.source}-${job.id}`}
                    job={job}
                    active={selected?.id === job.id && selected?.source === job.source}
                    onSelect={setSelectedJob}
                    onPractice={handlePracticeForJob}
                    onSave={handleSaveJob}
                  />
                ))}
              </div>
            </main>
          </div>
        </div>
      </div>
    </AppBackground>
  );
}
